import { useState, useCallback } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Upload, FileText, Link2, Trash2, Image, Video, Save, Loader2 } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import { sortByName } from "@/utils/sort";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function ModuleDetailsForm({ mod, setMod }) {
  return (
    <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8 space-y-5">
      <h2 className="font-heading text-xl text-[#5C3A2E] font-semibold">Module Details</h2>
      <div>
        <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Title</label>
        <Input data-testid="edit-module-title" value={mod.title || ""} onChange={(e) => setMod({ ...mod, title: e.target.value })} className="rounded-xl border-[#EADBC8]" />
      </div>
      <div>
        <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Description</label>
        <Textarea data-testid="edit-module-description" value={mod.description || ""} onChange={(e) => setMod({ ...mod, description: e.target.value })} rows={3} className="rounded-xl border-[#EADBC8] resize-none" />
      </div>
      <div>
        <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">What You'll Learn</label>
        <Textarea data-testid="edit-module-learn" value={mod.what_youll_learn || ""} onChange={(e) => setMod({ ...mod, what_youll_learn: e.target.value })} rows={4} className="rounded-xl border-[#EADBC8] resize-none" />
      </div>
      <div>
        <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Notes from Instructor</label>
        <RichTextEditor value={mod.notes || ""} onChange={(val) => setMod({ ...mod, notes: val })} placeholder="Write notes for your students..." />
      </div>
    </div>
  );
}

export function SyllabusSection({ mod, onSave }) {
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      await onSave({ syllabus_path: data.storage_path, syllabus_filename: data.original_filename });
    } catch {
      toast.error("Upload failed");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8">
      <h2 className="font-heading text-xl text-[#5C3A2E] font-semibold mb-4">PDF Syllabus</h2>
      {mod.syllabus_path ? (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F7F3EF]">
          <FileText className="w-5 h-5 text-[#5C3A2E]" />
          <span className="text-sm text-[#2B2B2B] flex-1">{mod.syllabus_filename || "Syllabus"}</span>
          <Button variant="ghost" size="sm" onClick={() => onSave({ syllabus_path: null, syllabus_filename: null })} className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      ) : (
        <p className="text-sm text-[#2B2B2B]/40 mb-3">No syllabus uploaded</p>
      )}
      <label className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#EADBC8] text-[#5C3A2E] rounded-full text-sm font-medium cursor-pointer hover:bg-[#EADBC8]/80 transition-colors">
        <Upload className="w-4 h-4" /> {mod.syllabus_path ? "Replace Syllabus" : "Upload Syllabus"}
        <input data-testid="syllabus-upload-input" type="file" accept=".pdf" onChange={handleUpload} className="hidden" />
      </label>
    </div>
  );
}

export function MaterialsSection({ mod, onSave }) {
  const [linkDialog, setLinkDialog] = useState(false);
  const [linkForm, setLinkForm] = useState({ name: "", url: "" });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");

  const handleBulkFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    let currentMats = [...(mod.materials || [])];
    let uploaded = 0;
    for (const file of files) {
      setUploadProgress(`Uploading ${uploaded + 1} of ${files.length}...`);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        currentMats = [...currentMats, { id: crypto.randomUUID(), type: "file", name: data.original_filename, storage_path: data.storage_path, content_type: data.content_type }];
        uploaded++;
      } catch {
        toast.error(`Failed: ${file.name}`);
      }
    }
    currentMats = sortByName(currentMats);
    await onSave({ materials: currentMats });
    setUploading(false);
    setUploadProgress("");
    if (uploaded > 0) toast.success(`${uploaded} file${uploaded > 1 ? "s" : ""} uploaded`);
    e.target.value = "";
  };

  const handleAddLink = async () => {
    const newMats = sortByName([...(mod.materials || []), { id: crypto.randomUUID(), type: "link", name: linkForm.name || linkForm.url, url: linkForm.url }]);
    await onSave({ materials: newMats });
    setLinkDialog(false);
    setLinkForm({ name: "", url: "" });
  };

  const removeMaterial = async (matId) => {
    await onSave({ materials: (mod.materials || []).filter((m) => m.id !== matId) });
  };

  const sortedMaterials = sortByName(mod.materials || []);

  return (
    <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8">
      <h2 className="font-heading text-xl text-[#5C3A2E] font-semibold mb-4">Resources & Materials</h2>
      <div className="space-y-2 mb-4">
        {sortedMaterials.map((mat) => (
          <div key={mat.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#F7F3EF]">
            {mat.type === "link" ? <Link2 className="w-4 h-4 text-[#C6A969]" /> : <FileText className="w-4 h-4 text-[#5C3A2E]" />}
            <span className="text-sm text-[#2B2B2B] flex-1">{mat.name}</span>
            <span className="text-xs text-[#2B2B2B]/30 uppercase">{mat.type}</span>
            <Button variant="ghost" size="sm" onClick={() => removeMaterial(mat.id)} className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        {sortedMaterials.length === 0 && <p className="text-sm text-[#2B2B2B]/40">No materials added yet</p>}
      </div>
      {uploading && (
        <div className="flex items-center gap-2 text-sm text-[#5C3A2E] mb-3">
          <Loader2 className="w-4 h-4 animate-spin" /> {uploadProgress}
        </div>
      )}
      <div className="flex gap-2">
        <label className={`inline-flex items-center gap-2 px-4 py-2 bg-[#EADBC8] text-[#5C3A2E] rounded-full text-sm font-medium cursor-pointer hover:bg-[#EADBC8]/80 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
          <Upload className="w-4 h-4" /> Upload Files
          <input data-testid="material-file-upload" type="file" multiple onChange={handleBulkFileUpload} className="hidden" />
        </label>
        <Button data-testid="add-link-button" variant="outline" onClick={() => setLinkDialog(true)} className="border-[#EADBC8] text-[#5C3A2E] rounded-full hover:bg-[#EADBC8]/30">
          <Link2 className="w-4 h-4 mr-1.5" /> Add Link
        </Button>
      </div>
      <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
        <DialogContent className="bg-white rounded-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#5C3A2E]">Add Link</DialogTitle>
            <DialogDescription className="text-[#2B2B2B]/50 text-sm">Add an external resource link</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Name</label>
              <Input data-testid="link-name-input" value={linkForm.name} onChange={(e) => setLinkForm({ ...linkForm, name: e.target.value })} placeholder="e.g., YouTube Tutorial" className="rounded-xl border-[#EADBC8]" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">URL</label>
              <Input data-testid="link-url-input" value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} placeholder="https://..." className="rounded-xl border-[#EADBC8]" />
            </div>
            <Button data-testid="save-link-button" onClick={handleAddLink} disabled={!linkForm.url} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">Add Link</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function VideoLinksSection({ mod, onSave }) {
  const [videoDialog, setVideoDialog] = useState(false);
  const [mode, setMode] = useState("single"); // "single" | "bulk"
  const [videoForm, setVideoForm] = useState({ title: "", url: "" });
  const [bulkText, setBulkText] = useState("");

  const handleAddSingle = async () => {
    const newVids = sortByName([...(mod.video_links || []), { id: crypto.randomUUID(), title: videoForm.title || videoForm.url, url: videoForm.url }], "title");
    await onSave({ video_links: newVids });
    setVideoDialog(false);
    setVideoForm({ title: "", url: "" });
  };

  const handleAddBulk = async () => {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
    if (!lines.length) return;
    let current = [...(mod.video_links || [])];
    let added = 0;
    for (const line of lines) {
      // Each line: "Title - URL" or just "URL"
      const dashIdx = line.indexOf(" - ");
      let title, url;
      if (dashIdx > 0) {
        title = line.slice(0, dashIdx).trim();
        url = line.slice(dashIdx + 3).trim();
      } else {
        url = line;
        title = line;
      }
      if (url.startsWith("http")) {
        current = [...current, { id: crypto.randomUUID(), title, url }];
        added++;
      }
    }
    await onSave({ video_links: sortByName(current, "title") });
    setVideoDialog(false);
    setBulkText("");
    if (added > 0) toast.success(`${added} video${added > 1 ? "s" : ""} added`);
    else toast.error("No valid URLs found. Each line should start with http");
  };

  const sortedVideos = sortByName(mod.video_links || [], "title");

  return (
    <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8">
      <h2 className="font-heading text-xl text-[#5C3A2E] font-semibold mb-4">Video Tutorials</h2>
      <div className="space-y-2 mb-4">
        {sortedVideos.map((v) => (
          <div key={v.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#F7F3EF]">
            <Video className="w-4 h-4 text-[#C6A969]" />
            <span className="text-sm text-[#2B2B2B] flex-1">{v.title || v.url}</span>
            <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#C6A969] hover:underline">Open</a>
            <Button variant="ghost" size="sm" onClick={async () => { await onSave({ video_links: (mod.video_links || []).filter((x) => x.id !== v.id) }); }} className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        {sortedVideos.length === 0 && <p className="text-sm text-[#2B2B2B]/40">No videos added yet</p>}
      </div>
      <Button data-testid="add-video-button" variant="outline" onClick={() => { setMode("single"); setVideoDialog(true); }} className="border-[#EADBC8] text-[#5C3A2E] rounded-full hover:bg-[#EADBC8]/30">
        <Video className="w-4 h-4 mr-1.5" /> Add Video Links
      </Button>
      <Dialog open={videoDialog} onOpenChange={setVideoDialog}>
        <DialogContent className="bg-white rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#5C3A2E]">Add Videos</DialogTitle>
            <DialogDescription className="text-[#2B2B2B]/50 text-sm">Add one or multiple video links</DialogDescription>
          </DialogHeader>
          <div className="flex gap-1.5 mb-3">
            <button onClick={() => setMode("single")} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${mode === "single" ? "bg-[#5C3A2E] text-white" : "bg-[#EADBC8]/30 text-[#2B2B2B]/60"}`}>Single</button>
            <button onClick={() => setMode("bulk")} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${mode === "bulk" ? "bg-[#5C3A2E] text-white" : "bg-[#EADBC8]/30 text-[#2B2B2B]/60"}`}>Bulk Paste</button>
          </div>
          {mode === "single" ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Title</label>
                <Input data-testid="video-title-input" value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} placeholder="e.g., How to thread a bobbin" className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Video URL</label>
                <Input data-testid="video-url-input" value={videoForm.url} onChange={(e) => setVideoForm({ ...videoForm, url: e.target.value })} placeholder="https://youtube.com/watch?v=..." className="rounded-xl border-[#EADBC8]" />
              </div>
              <Button data-testid="save-video-button" disabled={!videoForm.url} onClick={handleAddSingle} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">Add Video</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Paste video links (one per line)</label>
                <Textarea
                  data-testid="bulk-video-input"
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={6}
                  placeholder={"Title - https://youtube.com/...\nhttps://youtube.com/...\nAnother Title - https://vimeo.com/..."}
                  className="rounded-xl border-[#EADBC8] resize-none text-sm"
                />
                <p className="text-[10px] text-[#2B2B2B]/40 mt-1">Format: <span className="font-medium">Title - URL</span> or just <span className="font-medium">URL</span></p>
              </div>
              <Button data-testid="save-bulk-videos" disabled={!bulkText.trim()} onClick={handleAddBulk} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                Add All Videos
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function GallerySection({ mod, onSave }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    let currentImages = [...(mod.images || [])];
    let uploaded = 0;
    for (const file of files) {
      setUploadProgress(`Uploading ${uploaded + 1} of ${files.length}...`);
      const formData = new FormData();
      formData.append("file", file);
      try {
        const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        currentImages = [...currentImages, { id: crypto.randomUUID(), storage_path: data.storage_path, original_filename: data.original_filename, content_type: data.content_type }];
        uploaded++;
      } catch {
        toast.error(`Failed: ${file.name}`);
      }
    }
    currentImages = sortByName(currentImages, "original_filename");
    await onSave({ images: currentImages });
    setUploading(false);
    setUploadProgress("");
    if (uploaded > 0) toast.success(`${uploaded} image${uploaded > 1 ? "s" : ""} uploaded`);
    e.target.value = "";
  };

  const sortedImages = sortByName(mod.images || [], "original_filename");

  return (
    <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 sm:p-8">
      <h2 className="font-heading text-xl text-[#5C3A2E] font-semibold mb-4">Image Gallery</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
        {sortedImages.map((img) => (
          <div key={img.id} className="relative group aspect-square rounded-xl overflow-hidden bg-[#F7F3EF]">
            <img src={`${API_BASE}/api/files/${img.storage_path}`} alt={img.original_filename || "Gallery"} className="w-full h-full object-cover" />
            <p className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[10px] px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">{img.original_filename}</p>
            <div className="absolute top-1 right-1">
              <Button variant="ghost" size="sm" onClick={async () => { await onSave({ images: (mod.images || []).filter((x) => x.id !== img.id) }); }} className="opacity-0 group-hover:opacity-100 text-white bg-black/30 hover:bg-black/50 rounded-full h-7 w-7 p-0">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      {uploading && (
        <div className="flex items-center gap-2 text-sm text-[#5C3A2E] mb-3">
          <Loader2 className="w-4 h-4 animate-spin" /> {uploadProgress}
        </div>
      )}
      <label className={`inline-flex items-center gap-2 px-4 py-2 bg-[#EADBC8] text-[#5C3A2E] rounded-full text-sm font-medium cursor-pointer hover:bg-[#EADBC8]/80 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
        <Image className="w-4 h-4" /> Upload Images
        <input data-testid="gallery-upload-input" type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
      </label>
    </div>
  );
}
